import { Alert, Box, Button, Chip, Paper, Snackbar, TextField, Typography } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { useGooglePlacesAutocomplete } from '../../hooks/useGooglePlacesAutocomplete';
import { LeadsVoPartnerAgent } from '../../types/leadsVo';

export type RdvLocation = LeadsVoPartnerAgent['rdv_location'];

export const RDV_LOCATION_OPTIONS: { value: RdvLocation; label: string }[] = [
  { value: 'agency', label: 'En agence' },
  { value: 'home', label: 'Au domicile' },
  { value: 'flexible', label: 'Les deux' },
];

const chipSelected = { backgroundColor: '#262626', color: 'white', fontWeight: 600 };
const chipUnselected = {
  backgroundColor: 'white',
  color: '#262626',
  border: '1px solid #E0E0E0',
  '&:hover': { borderColor: '#FDBB5F' },
};

interface RdvLocationEditorProps {
  initialLocation: RdvLocation | null;
  initialAddress: string | null;
  onSave: (payload: { rdv_location: RdvLocation; agency_address?: string }) => Promise<unknown>;
}

// Lieu des RDV (agence, domicile du particulier, ou les deux) et adresse de l'agence,
// modifiables après l'onboarding depuis la page Mes critères.
const RdvLocationEditor = ({ initialLocation, initialAddress, onSave }: RdvLocationEditorProps) => {
  const [location, setLocation] = useState<RdvLocation>(initialLocation ?? 'agency');
  const [address, setAddress] = useState(initialAddress ?? '');
  // Dernière adresse issue de Google Places : une saisie manuelle qui en diverge est refusée
  // (il manque souvent CP et ville, et le partenaire ne peut pas géolocaliser).
  const [validatedAddress, setValidatedAddress] = useState(initialAddress ?? '');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const addressRef = useRef<HTMLInputElement>(null);
  const handleAddressSelect = useCallback((selected: string) => {
    setAddress(selected);
    setValidatedAddress(selected);
  }, []);
  useGooglePlacesAutocomplete(addressRef, handleAddressSelect);

  const showsAddress = location !== 'home';
  const typedAddress = address.trim();
  const needsAutocomplete = typedAddress.length > 0 && typedAddress !== validatedAddress.trim();

  const save = async () => {
    const isAddressMissing = location === 'agency' && !typedAddress;
    if (isAddressMissing) {
      setError("Adresse d'agence requise pour les RDV en agence.");
      return;
    }
    if (showsAddress && needsAutocomplete) {
      setError("Choisissez une adresse dans la liste de suggestions Google qui s'affiche en tapant.");
      return;
    }
    try {
      setSaving(true);
      setError('');
      await onSave({
        rdv_location: location,
        ...(showsAddress && typedAddress ? { agency_address: typedAddress } : {}),
      });
      setSuccess('Lieu de rendez-vous enregistré');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, borderRadius: 2, mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#262626' }}>Lieu de rendez-vous</Typography>
        <Button
          onClick={save}
          disabled={saving}
          sx={{
            backgroundColor: '#FDBB5F',
            color: '#262626',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { backgroundColor: '#fcae4d' },
          }}
        >
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box display="flex" gap={1} mb={showsAddress ? 2 : 0}>
        {RDV_LOCATION_OPTIONS.map((opt) => (
          <Chip
            key={opt.value}
            label={opt.label}
            onClick={() => setLocation(opt.value)}
            sx={{ cursor: 'pointer', fontSize: 13, px: 1, ...(location === opt.value ? chipSelected : chipUnselected) }}
          />
        ))}
      </Box>

      {/* Champ toujours monté (masqué pour « Au domicile ») : l'autocomplete Google reste branché sur le même input. */}
      <Box sx={{ display: showsAddress ? 'block' : 'none' }}>
        <TextField
          label={
            location === 'agency'
              ? "Adresse de l'agence (où les particuliers se rendent)"
              : "Adresse de l'agence (optionnel, pour les RDV en agence)"
          }
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          fullWidth
          required={location === 'agency'}
          inputRef={addressRef}
          placeholder="Commencez à taper pour rechercher"
          error={needsAutocomplete}
          helperText={needsAutocomplete ? 'Sélectionnez une adresse dans la liste de suggestions Google.' : ''}
        />
      </Box>

      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} message={success} />
    </Paper>
  );
};

export default RdvLocationEditor;
